import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const email = location.state?.email || params.get("email") || "";
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(location.state?.devCode || null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(30);

  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email, code });
      setUser(data.user);
      toast.success("Email verified — welcome to PaySignal!");
      navigate(data.user.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setResending(true); setError("");
    try {
      const { data } = await api.post("/auth/resend-code", { email });
      setDevCode(data.dev_code || null);
      setCooldown(30);
      toast.success("A new code is on its way.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell title="Confirm your email"
      subtitle={email ? `We sent a 6-digit code to ${email}.` : "Enter the code we emailed you."}
      footer={<>Wrong email? <Link to="/signup" className="text-slate-900 font-semibold underline" data-testid="verify-back-signup">Start over</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
        <div className="grid place-items-center">
          <span className="grid place-items-center w-12 h-12 rounded-full bg-brandblue-light text-brandblue"><MailCheck size={22} /></span>
        </div>
        {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="verify-error">{error}</div>}
        {devCode && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="verify-dev-code">
            Dev mode: your code is <span className="font-bold tracking-widest">{devCode}</span>. Add a Postmark key to send real emails.
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="code">6-digit code</Label>
          <Input id="code" inputMode="numeric" maxLength={6} required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            className="text-center text-2xl tracking-[0.5em] font-num"
            data-testid="verify-code-input" />
        </div>
        <Button type="submit" className="w-full" disabled={loading || code.length !== 6} data-testid="verify-submit">
          {loading ? "Verifying…" : "Verify & continue"}
        </Button>
      </form>
      <div className="mt-4 text-center text-sm text-slate-500">
        Didn't get it?{" "}
        <button onClick={resend} disabled={resending || cooldown > 0}
          className="text-slate-900 font-semibold underline disabled:no-underline disabled:text-slate-400 disabled:cursor-not-allowed"
          data-testid="verify-resend">
          {resending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </AuthShell>
  );
}
